import React, { useMemo } from "react";
import { Box } from "@mui/material";
import { format, parseJSON } from "date-fns";
import { DocumentTeamMember, DocumentTeamMemberRole } from "./teamMemberApi";
import { DocumentLanguages } from "../documentTypes";

type GroupKey = "Author" | "Reviewer" | "Approver" | "Other";

interface PrintPreviewTeamTableProps {
  teamMembers: DocumentTeamMember[];
  lang?: DocumentLanguages; // domyślnie PL
}

// Nagłówki tabeli
const TABLE_HEADERS = {
  PL: ["Imię i nazwisko", "Firma", "Stanowisko", "Rola", "Data"],
  EN: ["Full name", "Company", "Position", "Role", "Date"],
};

// Etykiety grup
const GROUP_LABELS_PL: Record<GroupKey, string> = {
  Author: "Opracował dokument",
  Reviewer: "Sprawdził dokument",
  Approver: "Zatwierdził dokument",
  Other: "Inne role",
};
const GROUP_LABELS_EN: Record<GroupKey, string> = {
  Author: "Prepared the document",
  Reviewer: "Reviewed the document",
  Approver: "Approved the document",
  Other: "Other roles",
};

// Role – PL
const ROLE_LABEL_PL: Record<DocumentTeamMemberRole, string> = {
  C_CLOSED: "Wcześniejszy autor",
  C: "Autor",
  E: "Edytor",
  P: "Przygotował",
  R: "Opiniujący",
  V: "Weryfikator",
  EXT_R: "Recenzent zewnętrzny",
  A: "Zatwierdzający",
  AR: "Zastępca zatwierdzającego",
};
// Role – EN
const ROLE_LABEL_EN: Record<DocumentTeamMemberRole, string> = {
  C_CLOSED: "Previous author",
  C: "Author",
  E: "Editor",
  P: "Preparer",
  R: "Reviewer",
  V: "Verifier",
  EXT_R: "External reviewer",
  A: "Approver",
  AR: "Deputy approver",
};

// Mapowanie ról do grup
const ROLE_TO_GROUP: Record<DocumentTeamMemberRole, GroupKey> = {
  C_CLOSED: "Author",
  C: "Author",
  E: "Author",
  P: "Author",
  R: "Reviewer",
  V: "Reviewer",
  EXT_R: "Reviewer",
  A: "Approver",
  AR: "Approver", // AR obsłużone osobno -> Reviewer + Approver
};

const GROUP_ORDER: GroupKey[] = ["Author", "Reviewer", "Approver"];

export const PrintPreviewTeamView: React.FC<PrintPreviewTeamTableProps> = ({
  teamMembers,
  lang = "PL",
}) => {
  const headers = TABLE_HEADERS[lang];
  const groupLabels = lang === "PL" ? GROUP_LABELS_PL : GROUP_LABELS_EN;
  const roleLabels = lang === "PL" ? ROLE_LABEL_PL : ROLE_LABEL_EN;

  const fmtDT = (d?: string | null) =>
    d ? format(parseJSON(d), "yyyy-MM-dd") : "-";

  // Grupowanie członków
  const grouped = useMemo(() => {
    const map = new Map<GroupKey, DocumentTeamMember[]>();
    GROUP_ORDER.forEach((k) => map.set(k, []));

    for (const tm of teamMembers) {
      if (tm.role === "AR") {
        // specjalny przypadek → w Reviewer i Approver
        map.get("Reviewer")!.push(tm);
        map.get("Approver")!.push(tm);
      } else {
        const g = ROLE_TO_GROUP[tm.role] ?? "Other";
        if (!map.has(g)) map.set(g, []);
        map.get(g)!.push(tm);
      }
    }

    return GROUP_ORDER.map((k) => ({
      key: k,
      label: groupLabels[k],
      items: map.get(k) ?? [],
    }));
  }, [teamMembers, lang]);

  // Render pojedynczego wiersza
  const renderRow = (
    tm: DocumentTeamMember,
    rowIndex: number,
    groupKey: GroupKey
  ) => {
    const date = fmtDT(tm.opinionDate ?? tm.approvementDate ?? null);

    let roleLabel: string;
    if (tm.role === "AR") {
      if (groupKey === "Reviewer") {
        roleLabel = lang === "PL" ? "Opiniujący" : "Reviewer";
      } else {
        roleLabel = lang === "PL" ? "Zatwierdzający" : "Approver";
      }
    } else {
      roleLabel = roleLabels[tm.role] ?? tm.role;
    }

    return (
      <tr key={tm.userName ?? `${tm.id}-${groupKey}-${rowIndex}`}>
        <td>
          {tm.firstName} {tm.surname}
        </td>
        <td>{tm.companyName || "-"}</td>
        <td>{tm.position || "-"}</td>
        <td>{roleLabel}</td>
        <td style={{ textAlign: "center" }}>{date}</td>
      </tr>
    );
  };

  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        p: 2,
        border: "1px solid #ddd",
        fontFamily: `Arial, "Helvetica Neue", Helvetica, sans-serif`,
        lineHeight: 1.6,
        "& table": {
          width: "100%",
          borderCollapse: "collapse",
          pageBreakInside: "auto",
        },
        "& th, & td": {
          border: "1px solid #000",
          padding: "6px 8px",
          verticalAlign: "top",
          fontSize: "0.95rem",
        },
        "& th": { textAlign: "left", fontWeight: 700 },
        "@media print": { border: "none", p: 0 },
      }}
    >
      <table>
        <thead>
          <tr>
            {headers.map((h, idx) => (
              <th key={idx}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {grouped.flatMap((group) => {
            if (!group.items.length) return [];
            const rows = group.items.map((tm, idx) =>
              renderRow(tm, idx + 1, group.key)
            );
            return [
              <tr key={`hdr-${group.key}`}>
                <td
                  colSpan={headers.length}
                  style={{ fontWeight: 700, background: "#f6f6f6" }}
                >
                  {group.label}
                </td>
              </tr>,
              ...rows,
            ];
          })}
        </tbody>
      </table>
    </Box>
  );
};
